import React, { useCallback, useMemo, useState } from 'react';
import { map } from 'lodash';

import { scaleColorTraffic } from 'js/utils/color';

import CategoryPills from '../CategoryPills';
import CircleChart from './CircleChart';
import AssessmentSubview from './AssessmentSubview';
import QuestionSetSubview from './QuestionSetSubview';
import QuestionSubview from './QuestionSubview';
import { radialPieWedges } from './CircleChart/radialPieWedges';

const AssessmentHistoryRadialPieChart = ({
  dataMap,
  assessmentId,
  categoryColorMap,
  categoryLookups,
}) => {
  const questionSets = useMemo(
    () =>
      assessmentId
        ? dataMap?.assessments?.[assessmentId]?.questionSets ?? {}
        : dataMap?.questionSets ?? {},
    [dataMap, assessmentId]
  );

  const categories = [...new Set(map(questionSets, (qs) => qs.category))];

  const { flattenToQuestions } = useMemo(
    () => radialPieWedges(questionSets),
    [questionSets]
  );

  const [selectedSlice, setSelectedSlice] = useState(null);

  const handleSelectSlice = useCallback((slice) => setSelectedSlice(slice), []);

  const handleClearSlices = useCallback(() => setSelectedSlice(null), []);

  const sliceDetail = (() => {
    if (selectedSlice == null) {
      return (
        <AssessmentSubview
          questionSets={questionSets}
          categoryLookups={categoryLookups}
          categoryColorMap={categoryColorMap}
        />
      );
    }

    if (flattenToQuestions || selectedSlice.kind === 'question') {
      return (
        <QuestionSubview
          question={selectedSlice.question}
          categoryLookups={categoryLookups}
          categoryColorMap={categoryColorMap}
        />
      );
    }

    return (
      <QuestionSetSubview
        questionSet={selectedSlice.payload}
        categoryLookups={categoryLookups}
        categoryColorMap={categoryColorMap}
      />
    );
  })();

  return (
    <>
      <div className="row">
        <div className="col">
          <CategoryPills
            categories={categories}
            categoryLookups={categoryLookups}
          />
        </div>
      </div>
      <div className="row">
        <div className="col">
          <div className="assessment-history-chart">
            <div className="assessment-history-radial-pie-figure">
              <div className="figure-radial-pie">
                <CircleChart
                  questionSets={questionSets}
                  categoryLookups={categoryLookups}
                  categoryColorMap={categoryColorMap}
                  onSelectSlice={handleSelectSlice}
                  onClearSlices={handleClearSlices}
                />
              </div>
              {sliceDetail}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

AssessmentHistoryRadialPieChart.defaultProps = {
  colors: {
    domain: scaleColorTraffic.domain(),
    range: scaleColorTraffic.range(),
  },
};

export const Glyph = (
  <svg
    viewBox="0 0 32 32"
    className="chart-glyph assessment-history-radial-pie-chart-glyph"
  >
    <g className="radial-pie-glyphs">
      <g className="radial-pie-slices">
        <path
          d="M 16 16 L 16 2 A 14 14 0 0 1 28.124355652982143 23 Z"
          className="radial-pie-slice palette-cat-1"
        ></path>
        <path
          d="M 16 16 L 24.66025403784439 21 A 10 10 0 0 1 7.339745962155613 21 Z"
          className="radial-pie-slice palette-cat-2"
        ></path>
        <path
          d="M 16 16 L 4.741669750802297 22.5 A 13 13 0 0 1 15.999999999999998 3 Z"
          className="radial-pie-slice palette-cat-3"
        ></path>
      </g>
      <g className="radial-pie-ring">
        <path
          className="radial-pie-ring-arc stroke-only palette-cat-1"
          d="M 16 1 A 15 15 0 0 1 28.99038105676658 23.5"
        ></path>
        <path
          className="radial-pie-ring-arc stroke-only palette-cat-2"
          d="M 28.99038105676658 23.5 A 15 15 0 0 1 3.00961894323342 23.5"
        ></path>
        <path
          className="radial-pie-ring-arc stroke-only palette-cat-3"
          d="M 3.00961894323342 23.5 A 15 15 0 0 1 15.999999999999996 1"
        ></path>
      </g>
    </g>
    <g className="radial-pie-separators">
      <line
        className="radial-pie-separator"
        x1="16"
        y1="16"
        x2="16"
        y2="0"
      ></line>
      <line
        className="radial-pie-separator"
        x1="16"
        y1="16"
        x2="29.85640646055102"
        y2="24"
      ></line>
      <line
        className="radial-pie-separator"
        x1="16"
        y1="16"
        x2="2.1435935394489807"
        y2="24"
      ></line>
    </g>
  </svg>
);

export default AssessmentHistoryRadialPieChart;
